/**
 * RefundApplication
 * --
 */
import { useState } from 'react';
import {
  Button,
  Table,
  Tag,
  Descriptions,
  Input,
  Divider,
  Flex,
  InputNumber,
} from 'antd';
import { Content, Title, ModalLayout, Text } from 'components';
import moment from 'moment';
import MessageAlert, { stringToMoneyFormat } from 'utils';
import Formatter from 'utils/Formatter';
// import { JM_PAY_TYPE } from 'constants/JMPayConstant';
import { InquiryApi } from 'api';
import { useUserStack } from 'store';
import {
  CheckCircleOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { downloadXlsx } from 'utils/XlsxDownloader';
import Logger from 'utils/logger';
import { useRefundChargeJmPay } from 'hooks/JmPayHooks';
import Alert from 'utils/AlertManager';

const SIGNUP_PATH = {
  MEMBER: '일반가입',
  NAVER: '네이버',
  GOOGLE: '구글',
  APPLE: '애플',
  KAKAO: '카카오',
  NONE: '비회원',
};

const USER_SETTING = {
  location_consent: '위치정보 서비스 이용약관 동의',
  marketing_consent: '마케팅정보 수집 및 활용 동의',
  push_notification: '푸시알림',
  event_notification: '프로모션/이벤트 알림',
  terms_of_use: '이용약관 동의 여부',
  terms_of_privacy: '개인정보 수집 및 이용 동의 여부',
  terms_of_marketing: '마케팅 목적의 개인정보 수집 및 이용 동의 여부',
  terms_of_ad: '혜택 및 광고성 정보 수신 동의',
  terms_of_age: '만 14세 이하',
};

const logger = new Logger('RefundApplication');

/**----------------------------------------
 * [Component] JM페이 > 유저목록
 * ----------------------------------------
 */
const RefundApplication = ({ data, currentRow, onSelect, onSaveAnswer }) => {
  /* ===== STATE ===== */
  const userInfo = useUserStack();
  const [, setLoading] = useState(false);
  const [expansionUser, setExpansionUser] = useState(false);
  const [answer, setAnswer] = useState(currentRow?.inquiry_answer ?? '');
  const [isVisible, setIsVisible] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [deductPoint, setDeductPoint] = useState(0);

  /* ===== VARIABLE ===== */
  const { refundChargeJmPay } = useRefundChargeJmPay();
  /* ===== FUNCTION ===== */

  const col = [
    {
      title: '요청일(시간)',
      dataIndex: 'created_at',
      width: 135,
      align: 'center',
      render: (value) => moment(value * 1000).format('YYYY-MM-DD (HH:mm:ss)'),
      sorter: (a, b) => a.created_at - b.created_at,
    },
    {
      title: '요약',
      dataIndex: 'inquiry_name',
      width: 230,
      align: 'center',
    },
    {
      title: '요청내용',
      dataIndex: 'inquiry_content',
      width: 300,
      align: 'center',
      render: (value) => (
        <p
          style={{
            minHeight: 70,
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
          }}
          dangerouslySetInnerHTML={{
            __html: value,
          }}
        ></p>
      ),
    },
    {
      title: '회원명',
      dataIndex: 'user',
      width: 160,
      align: 'center',
      render: (value) => (
        <a style={{ textDecoration: 'underline' }}>{`${value?.user_name}(${
          value?.user_gender
        }, ${value?.user_nickname ?? '-'})`}</a>
      ),
    },
    {
      title: '연락처',
      dataIndex: 'user',
      width: 160,
      align: 'center',
      render: (value) => value?.user_phone,
    },

    {
      title: '처리여부',
      dataIndex: 'inquiry_answer',
      // width: 150,
      align: 'center',
      render: (value) => (value ? '처리완료' : '-'),
      filters: [
        { text: '처리완료', value: 'COMPLETED' },
        { text: '미처리', value: 'PENDING' },
      ],
      onFilter: (value, record) => {
        if (value === 'COMPLETED') {
          return !!record?.inquiry_answer;
        }
        if (value === 'PENDING') {
          return !record?.inquiry_answer;
        }
        return false;
      },
    },
    {
      title: '처리시간',
      dataIndex: 'updated_at',
      width: 135,
      align: 'center',
      render: (value, row) =>
        value !== row.created_at
          ? moment(value * 1000).format('YYYY-MM-DD (HH:mm:ss)')
          : '-',
    },
    {
      title: '상세보기',
      dataIndex: 'inquiry_answer',
      width: 100,
      align: 'center',
      render: (value) => <Button size="small">상세보기</Button>,
    },
    {
      title: 'JM페이 차감',
      dataIndex: 'deducted',
      width: 100,
      align: 'center',
      render: (value, record) => (
        <Button
          size="small"
          danger
          onClick={(e) => {
            setIsVisible(true);
            setTargetUser(record.user);
            e.stopPropagation();
          }}
        >
          페이차감
        </Button>
      ),
    },
  ];
  /**--------------------
   * 답변저장
   * --------------------
   */
  const handleSaveAnswer = async () => {
    // 예외처리
    if (!answer) {
      MessageAlert.warning('입력데이터를 확인해주세요');
      return;
    }
    if (
      !window.confirm(
        '회신 내용을 저장할 경우 처리완료처리됩니다.\n저장하시겠습니까?'
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      const newData = {
        inquiry_id: currentRow.inquiry_id,
        inquiry_answer: answer,
        inquiry_answer_id: `${userInfo.admin_id}`,
        updated_at: moment().unix(),
      };
      // API
      const { status, message } = await InquiryApi.updateInquiry(newData);
      if (status !== 200) throw { message };

      MessageAlert.info('저장되었습니다.');
      onSaveAnswer({
        ...currentRow,
        ...newData,
      });
      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**
   * 제이엠페이 차감
   * --
   */
  const handleDeductedPoint = async () => {
    if (deductPoint > targetUser.jm_pay) {
      MessageAlert.info('보유중인 JM페이보다 차감금액이 큽니다!');
      return;
    }

    if (
      !Alert.confirm(
        `정말 ${targetUser.user_name} 님의 JM 페이를 ${stringToMoneyFormat(
          deductPoint
        )}원만큼 차감하시겠습니까?`
      )
    )
      return;

    try {
      const deductData = {
        user_id: targetUser.user_id,
        price: deductPoint,
        type: 'DEDUCTED',
      };
      const { status, message } = await refundChargeJmPay(deductData);

      if (status !== 200) {
        MessageAlert.warning('제이엠페이 차감에 실패하였습니다.');
        logger.warn('handleDeductedPoint', message);
        return;
      }

      setIsVisible(false);
      setTargetUser(null);

      MessageAlert.success(
        `성공적으로 ${stringToMoneyFormat(deductPoint)}원만큼 차감되었습니다.`
      );
    } catch (err) {
      MessageAlert.error('차감실패!');
      logger.error('handleDeductedPoint', err);
    }
  };
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  const filteredData = !data
    ? []
    : data
        .sort((a, b) => b.created_at - a.created_at)
        .sort((a, b) => (a.inquiry_answer === null ? 1 : -1));

  // JSX: start
  return (
    <>
      <Content maxWidth={'100%'} padding={0} backgroundColor={'none'}>
        {/* === 상단 > 타이틀 === */}
        <Title
          size={5}
          style={{ marginBottom: 7.5 }}
          subButton={
            <Button
              onClick={() =>
                downloadXlsx({
                  columns: col?.map((column) => {
                    if (column?.title === '연락처') {
                      column.dataIndex = 'user_phone';
                    }
                    if (column?.title === '처리여부') {
                      column.dataIndex = 'is_completed';
                    }
                    return column;
                  }),
                  rows: data?.map((_item) => {
                    const user = _item?.user;
                    return {
                      ..._item,
                      created_at: moment(_item?.created_at * 1000).format(
                        'YYYY-MM-DD (HH:mm:ss)'
                      ),
                      user: `${user?.user_name}(${user?.user_gender}, ${
                        user?.user_nickname ?? '-'
                      })`,
                      user_phone: user?.user_phone,
                      is_completed:
                        _item?.inquiry_answer === null ||
                        _item?.inquiry_answer === ''
                          ? '-'
                          : '처리완료',
                    };
                  }),
                  fileName: '제이엠페이_잔액환불신청_내역',
                })
              }
            >
              엑셀로 다운로드
            </Button>
          }
        >
          환불신청({data?.length ?? 0})
        </Title>

        {/* ===== 데이터 테이블: start ===== */}
        <div style={{ overflow: 'auto', width: '100%', height: '100%' }}>
          <Table
            className="boxShadow"
            bordered
            size="small"
            dataSource={filteredData ?? []}
            scroll={{
              x: 'max-content',
            }}
            style={{ minHeight: 580 }}
            onRow={(record) => {
              return {
                onClick: () => onSelect(record), // 행 클릭 시 이벤트 처리
              };
            }}
            columns={col}
          />
        </div>
        {/* ===== 데이터 테이블: end ===== */}
      </Content>

      {/* ===== 상세모달: start ===== */}
      <ModalLayout
        // type="drawer"
        // centered
        // maxWidth={710}
        width={700}
        minHeight={600}
        isOpen={currentRow !== null}
        open={currentRow !== null}
        title="주문상세"
        closable={true}
        onCancel={() => onSelect(null)}
        footer={
          <>
            <Button
              onClick={() => {
                setExpansionUser(false);
                onSelect(null);
              }}
            >
              닫기
            </Button>
          </>
        }
      >
        <Divider style={{ margin: '13px 0' }} />
        {currentRow && (
          <Content maxWidth={'100%'} padding={0}>
            {/* === 주문자 정보: start === */}
            <Title size={5} style={{ marginBottom: 5 }}>
              환불신청정보
            </Title>
            <Descriptions
              bordered
              column={2}
              size="small"
              labelStyle={styles.labelStyle}
              contentStyle={styles.contentStyle}
              style={styles.defaultStyle}
              items={[
                // === Line 1 === //
                {
                  key: `key11`,
                  label: '신청요약',
                  span: 2,
                  children: currentRow?.inquiry_name,
                },
                {
                  key: `key12`,
                  label: '신청내용',
                  span: 2,
                  children: (
                    <p
                      style={{
                        minHeight: 70,
                        textAlign: 'left',
                        whiteSpace: 'pre-wrap',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: currentRow?.inquiry_content,
                      }}
                    ></p>
                  ),
                },
                {
                  key: `key13`,
                  label: '신청일',
                  children: moment(currentRow?.created_at * 1000).format(
                    'YYYY-MM-DD (HH:mm:ss)'
                  ),
                },
                // === Line 2 === //
                {
                  key: `key14`,
                  label: '처리시간',
                  children:
                    currentRow?.inquiry_answer !== null &&
                    moment(currentRow?.updated_at * 1000).format(
                      'YYYY-MM-DD (HH:mm:ss)'
                    ),
                },
                {
                  key: `key15`,
                  label: '회신',
                  span: 2,
                  children: (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            width: '85%',
                          }}
                        >
                          <Input.TextArea
                            placeholder="내용을 입력해주세요"
                            rows={2}
                            value={answer ?? ''}
                            onChange={(e) => setAnswer(e.target.value)}
                          ></Input.TextArea>{' '}
                        </div>
                        <div
                          style={{
                            width: '14%',
                          }}
                        >
                          <Button
                            size="large"
                            type="primary"
                            block
                            style={{
                              height: '100%',
                            }}
                            onClick={handleSaveAnswer}
                          >
                            저장
                          </Button>
                        </div>
                      </div>
                    </>
                  ),
                },
              ]}
            />
            {/* === 주문자 정보: end === */}
            <br />
            <br />

            <Title size={5} style={{ marginBottom: 5 }}>
              회원 정보
            </Title>
            <Descriptions
              bordered
              column={2}
              size="small"
              labelStyle={styles.labelStyle}
              contentStyle={styles.contentStyle}
              style={styles.defaultStyle}
              items={[
                {
                  key: '1',
                  label: '회원번호',
                  children: `${currentRow?.user?.user_id}`,
                },
                {
                  key: '2',
                  label: '회원명',
                  children: `${currentRow?.user?.user_name ?? '-'}`,
                },
                {
                  key: '3',
                  label: '회원 아이디',
                  children: `${currentRow?.user?.user_account ?? '-'}`,
                },
                {
                  key: '4',
                  label: '보유 JM 페이',
                  children: stringToMoneyFormat(currentRow?.user?.jm_pay),
                },
                {
                  key: '5',
                  label: '연락처',
                  children: `${currentRow?.user?.user_phone ?? '-'}`,
                },
                {
                  key: '6',
                  label: '이메일',
                  children: `${currentRow?.user?.user_email ?? '-'}`,
                },

                // 확장 버튼 클릭 시 열리는 정보
                ...(expansionUser === true
                  ? [
                      {
                        key: '7',
                        label: '성별',
                        children: `${
                          currentRow?.user?.user_gender === 'M' ? '남' : '여'
                        }`,
                      },
                      {
                        key: '8',
                        label: '생년월일',
                        children: `${Formatter.unixToDate(
                          currentRow?.user?.user_birthday,
                          'YYYY.MM.DD'
                        )}`,
                      },
                      {
                        key: '18',
                        label: '회원등급',
                        children: `${
                          currentRow?.user?.grade?.grade_name ?? '-'
                        }`,
                      },
                      {
                        key: '17',
                        label: '메모',
                        children: currentRow?.user?.memo,
                      },
                      {
                        key: '9',
                        label: '주소',
                        children: `${currentRow?.user?.user_address ?? '-'}\n${
                          currentRow?.user?.user_address_detail ?? ''
                        }`,
                        span: 2,
                      },
                      {
                        key: '10',
                        label: '닉네임',
                        children: `${currentRow?.user?.user_nick_name ?? '-'}`,
                      },
                      {
                        key: '11',
                        label: '멤버십 번호',
                        children: `${currentRow?.user?.membership_code ?? '-'}`,
                      },
                      {
                        key: '12',
                        label: '가입일',
                        children: `${Formatter.unixToDate(
                          currentRow?.user?.created_at,
                          'YYYY.MM.DD HH:mm:ss'
                        )}`,
                      },
                      {
                        key: '13',
                        label: '최근 접속일',
                        children: `${Formatter.unixToDate(
                          currentRow?.user?.access_time,
                          'YYYY.MM.DD HH:mm:ss'
                        )}`,
                      },
                      {
                        key: '14',
                        label: '가입경로',
                        children: `${
                          currentRow?.user?.social_type
                            ? SIGNUP_PATH[currentRow?.user?.social_type]
                            : currentRow?.user?.user_name
                            ? SIGNUP_PATH['MEMBER']
                            : SIGNUP_PATH['NONE']
                        }`,
                      },
                      {
                        key: '15',
                        label: '보유쿠폰',
                        children: (
                          <p>
                            <button
                              style={{
                                border: 'none',
                                background: 'none',
                                color: '#1C6BFF',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                // setIsOpened(!isOpened);
                                // setModalType('COUPON');
                              }}
                            >
                              {stringToMoneyFormat(
                                currentRow?.user?.couponStorage?.useable
                                  ?.length,
                                '개'
                              )}
                            </button>
                          </p>
                        ),
                      },
                      {
                        key: '16',
                        label: '회원설정',
                        children: (
                          <div>
                            {Object.entries(
                              currentRow?.user?.user_setting || {}
                            ) // user_setting이 없으면 빈 객체 처리
                              .map(([key, value], index) => {
                                if (!USER_SETTING[key]) return null; // 조건이 충족되지 않으면 null 반환

                                return (
                                  <p
                                    style={{
                                      // margin: '8px 0px',
                                      margin: 0,
                                      padding: '5px 0',
                                      textAlign: 'left',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      borderTop:
                                        index > 0
                                          ? '1px solid #f1f1f1'
                                          : 'none',
                                    }}
                                  >
                                    <p style={{ margin: 0 }}>
                                      {USER_SETTING[key]}:
                                    </p>{' '}
                                    <p style={{ margin: 0 }}>
                                      {value ? (
                                        <Tag
                                          icon={<CheckCircleOutlined />}
                                          color="success"
                                        >
                                          동의
                                        </Tag>
                                      ) : (
                                        <Tag
                                          icon={<MinusCircleOutlined />}
                                          color="default"
                                        >
                                          미동의
                                        </Tag>
                                      )}
                                    </p>
                                    {/* <p>{value ? '동의' : '미동의'}</p> */}
                                  </p>
                                );
                              })}
                          </div>
                        ),
                        span: 2,
                      },
                    ]
                  : []),
              ]}
            />
            <div>
              {expansionUser !== true ? (
                <Button
                  type="link"
                  block
                  onClick={() => setExpansionUser(true)}
                >
                  <PlusCircleOutlined /> 내용 상세보기
                </Button>
              ) : (
                <Button
                  type="link"
                  block
                  onClick={() => setExpansionUser(false)}
                >
                  <MinusCircleOutlined /> 내용 접기
                </Button>
              )}
            </div>
          </Content>
        )}
        <Divider style={{ margin: '13px 0' }} />
      </ModalLayout>
      {/* ===== 상세모달: end ===== */}

      {/* ===== 페이 차감: start ===== */}
      <ModalLayout
        width={500}
        minHeight={300}
        open={isVisible && targetUser}
        title="JM페이 차감"
        closable={true}
        onCancel={() => {
          setTargetUser(null);
          setIsVisible(false);
        }}
        afterClose={() => {
          setTargetUser(null);
          setIsVisible(false);
        }}
        footer={
          <Flex justify="flex-end" align="center" gap={8}>
            <Button
              onClick={() => {
                setTargetUser(null);
                setIsVisible(false);
              }}
            >
              닫기
            </Button>
            <Button danger onClick={() => handleDeductedPoint()}>
              차감
            </Button>
          </Flex>
        }
      >
        <Content maxWidth={'100%'} padding={0}>
          <Flex flex={1} vertical gap={8}>
            <Flex gap={4} align="center">
              <Text>{targetUser?.user_name} 님</Text>
              <Text size={14} color={'#666666'}>
                현재 보유 포인트: {stringToMoneyFormat(targetUser?.jm_pay)}
              </Text>
            </Flex>

            <InputNumber
              size="large"
              min={0}
              max={targetUser?.jm_pay}
              placeholder="차감할 JM페이 금액을 입력해주세요."
              controls={false}
              style={{ width: '100%' }}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              value={deductPoint}
              onChange={(e) => setDeductPoint(e)}
            />
          </Flex>
        </Content>
      </ModalLayout>
      {/* ===== 페이 차감: end ===== */}
    </>
  ); // JSX: end
};

/*  */
const styles = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 20,
  },
  button: {
    width: 80,
    height: 40,
    fontSize: 15,
    fontWeight: 600,
    margin: '0px 4px',
  },
  form: {
    input: {
      large: {
        width: 175,
        marginRight: 8,
      },
      medium: {
        width: 100,
        marginRight: 8,
      },
    },
    button: {
      marginRight: 8,
    },
    date: {
      width: 150,
    },
    text: {
      fontSize: 13,
      fontWeight: 400,
    },
  },

  couponLayout: {
    width: '100%',
    backgroundColor: '#f4f4f4',
    borderLeft: '20px solid #ddd',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: 20,
    paddingLeft: 20,
    position: 'relative',
    top: 0,
    left: 0,
  },
  couponTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 16,
  },
  couponStatus: {
    color: 'var(--black-color)',
    fontSize: 16,
    lineHeight: 1.75,
  },
  couponDate: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--dark-gray-color)',
  },

  labelStyle: {
    // width: 155,
    width: '21%',
    color: '#111',
    fontWeight: '500',
    background: '#f4f4f4',
  },
  contentStyle: { width: '29%' },
  defaultStyle: {
    borderTop: '2px solid #d1d1d1',
    borderRadius: 5,
    marginTop: 5,
  },
};

/* DF */
RefundApplication.defaultProps = {
  onSelect: () => {},
  onSaveAnswer: () => {},
};
export default RefundApplication;
